/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import { paragraphXSmall, weightBold, weightMedium } from 'src/components/css'

/** Lets the link's hover recolour the name alone, not the initials avatar. */
const NAME_CLASS = 'roster-member-name'

interface RosterMemberCellProps {
  name: string
  initials: string
  imageUrl?: string
  /** Makes the whole cell a link — the Team screen opens the member's profile. */
  to?: string
  /** A muted second line under the name, e.g. the customer identifier. */
  subtitle?: string
}

/** Round avatar + name, matching the artifact's roster row treatment. */
const RosterMemberCell = ({
  name,
  initials,
  imageUrl,
  to,
  subtitle,
}: RosterMemberCellProps) => {
  'use memo'

  // A dead image URL would otherwise render the browser's broken-image glyph;
  // falling back to initials keeps every row looking deliberate.
  const [failed, setFailed] = useState(false)
  const showImage = Boolean(imageUrl) && !failed

  const content = (
    <>
      {showImage ? (
        <img
          css={avatarStyle}
          src={imageUrl}
          alt=''
          onError={() => setFailed(true)}
        />
      ) : (
        <span css={avatarStyle} aria-hidden>
          {initials}
        </span>
      )}
      <span css={textStyle}>
        <span className={NAME_CLASS} css={nameStyle}>
          {name}
        </span>
        {subtitle ? <span css={subtitleStyle}>{subtitle}</span> : null}
      </span>
    </>
  )

  return to ? (
    <Link to={to} css={[rowStyle, linkStyle]}>
      {content}
    </Link>
  ) : (
    <span css={rowStyle}>{content}</span>
  )
}

/* Full cell height so the avatar and text block centre in the row — the text
   block sets its own line-height, so it no longer inherits the row's. */
const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  height: 100%;
`

const avatarStyle = css`
  ${weightBold}
  width: 26px;
  height: 26px;
  flex: none;
  border-radius: 50%;
  background: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
  display: flex;
  align-items: center;
  justify-content: center;
  /* contain, not cover — an uploaded avatar must not be cropped oddly */
  object-fit: contain;
  font-size: 10px;
`

const linkStyle = css`
  &:hover .${NAME_CLASS} {
    color: ${colors.PRIMARY};
  }
`

const textStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
`

const nameStyle = css`
  ${weightMedium}
  color: ${colors.NEUTRAL_0};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const subtitleStyle = css`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export default RosterMemberCell
